import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ConfirmDialog } from "./Modal.jsx";
import { useVault } from "../hooks/useVault.jsx";
import { useToast } from "../hooks/useToast.jsx";
import { detect, fmtDate, toInputValue } from "../lib/dates.js";
import { generatePassword, passwordStrength } from "../lib/crypto.js";
import * as AI from "../lib/ai.js";
import * as Biometric from "../lib/biometric.js";
import {
  MicIcon, SparkleIcon, TrashIcon, CopyIcon, EyeIcon, EyeOffIcon,
  RefreshIcon, KeyIcon, BellIcon, CheckIcon, FingerprintIcon,
} from "./Icons.jsx";

const CATEGORIES = [
  { id: "diary", label: "Diary" },
  { id: "journal", label: "Journal" },
  { id: "voice", label: "Voice Note" },
  { id: "password", label: "Password" },
];

const blank = (category = "diary") => ({
  category,
  title: "",
  content: "",
  tags: [],
  username: "",
  url: "",
  password: "",
  alertEnabled: false,
  alertDate: "",
});

export function Editor({ entry, defaultCategory, onClose }) {
  const { put, remove, verifyPassword, setLockSuspended, setting, SETTING_IDS } = useVault();
  const toast = useToast();

  const isNew = !entry?.id;
  const [draft, setDraft] = useState(() => ({ ...blank(defaultCategory), ...(entry || {}) }));
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(null);
  const [tagInput, setTagInput] = useState("");

  // A saved password starts hidden and is not even placed in the DOM until the
  // user re-authenticates — see revealPassword below.
  const [revealed, setRevealed] = useState(isNew || draft.category !== "password");
  const [reauth, setReauth] = useState(false);

  const aiSettings = setting(SETTING_IDS.preferences, {});
  const aiReady = AI.isConfigured(aiSettings);

  // Hold off the idle auto-lock while this dialog is open.
  useEffect(() => {
    setLockSuspended(true);
    return () => setLockSuspended(false);
  }, [setLockSuspended]);

  const update = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  // --- dates ---------------------------------------------------------------
  const detected = useMemo(
    () => detect(`${draft.title} ${draft.content}`),
    [draft.title, draft.content]
  );

  // Offer the day before the first date found, unless a reminder is already set.
  useEffect(() => {
    if (draft.alertEnabled || !detected.length) return;
    const suggestion = toInputValue(detected[0].alertDate);
    if (draft.suggestedAlert === suggestion) return;
    setDraft((d) => ({ ...d, suggestedAlert: suggestion }));
  }, [detected, draft.alertEnabled, draft.suggestedAlert]);

  // --- voice ---------------------------------------------------------------
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("This browser has no speech recognition.");

    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = navigator.language || "en-US";
    rec.onresult = (e) => {
      const said = Array.from(e.results).slice(e.resultIndex)
        .map((r) => r[0].transcript).join(" ").trim();
      if (said) setDraft((d) => ({ ...d, content: `${d.content}${d.content ? " " : ""}${said}` }));
      setDirty(true);
    };
    rec.onerror = () => { setListening(false); toast.error("Speech recognition stopped."); };
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  useEffect(() => () => recognitionRef.current?.stop?.(), []);

  // --- AI assists ----------------------------------------------------------
  const runAssist = async (kind) => {
    const text = `${draft.title}\n\n${draft.content}`.trim();
    if (text.length < 20) return toast.warn("Write a little more first.");
    setBusy(kind);
    try {
      if (kind === "summarize") {
        const summary = await AI.summarize(aiSettings, text);
        update({ content: `${draft.content}\n\n— Summary —\n${summary}` });
        toast.success("Summary added");
      } else if (kind === "tags") {
        const raw = await AI.suggestTags(aiSettings, text);
        const fresh = raw.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
        update({ tags: [...new Set([...(draft.tags || []), ...fresh])] });
        toast.success("Tags suggested");
      } else if (kind === "title") {
        update({ title: (await AI.suggestTitle(aiSettings, text)).replace(/^["']|["']$/g, "") });
        toast.success("Title suggested");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  // --- tags ----------------------------------------------------------------
  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    if (!(draft.tags || []).includes(t)) update({ tags: [...(draft.tags || []), t] });
    setTagInput("");
  };

  // --- save / close --------------------------------------------------------
  const save = async () => {
    if (!draft.title.trim() && !draft.content.trim() && !draft.password) {
      return toast.warn("Add a title or some content first.");
    }
    setBusy("save");
    try {
      const { suggestedAlert, ...payload } = draft;
      await put("entry", {
        ...payload,
        tags: draft.tags || [],
        createdAt: draft.createdAt || Date.now(),
        updatedAt: Date.now(),
      }, entry?.id || null);
      toast.success(isNew ? "Entry saved" : "Entry updated");
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const requestClose = () => (dirty ? setConfirmDiscard(true) : onClose());

  const revealPassword = async (password) => {
    if (!(await verifyPassword(password))) throw new Error("That password is not correct.");
    setRevealed(true);
    setReauth(false);
  };

  const isPassword = draft.category === "password";
  const strength = passwordStrength(draft.password || "");

  return (
    <>
      <Modal
        title={isNew ? "New entry" : draft.title || "Edit entry"}
        subtitle={draft.createdAt ? `Created ${fmtDate(new Date(draft.createdAt))}` : "Not saved yet"}
        onClose={requestClose}
        size="wide"
        footer={
          <>
            {!isNew && (
              <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                <TrashIcon size={15} /> Delete
              </button>
            )}
            <div className="grow" />
            <button className="btn btn-ghost" onClick={requestClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={busy === "save"}>
              {busy === "save" ? <span className="spinner" /> : <CheckIcon size={15} />} Save
            </button>
          </>
        }
      >
        <div className="field-row">
          <div className="field" style={{ maxWidth: 180 }}>
            <label htmlFor="ed-cat">Category</label>
            <select id="ed-cat" value={draft.category} onChange={(e) => update({ category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ed-title">Title</label>
            <input id="ed-title" type="text" value={draft.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder={isPassword ? "e.g. Bank login" : "Give this entry a title"} />
          </div>
        </div>

        {isPassword && (
          <div className="panel" style={{ padding: "var(--s-4)", background: "var(--surface-2)" }}>
            <div className="panel-title"><KeyIcon size={12} /> Credentials</div>

            <div className="field-row" style={{ marginBottom: "var(--s-3)" }}>
              <div className="field">
                <label htmlFor="ed-user">Username</label>
                <input id="ed-user" type="text" value={draft.username || ""}
                  onChange={(e) => update({ username: e.target.value })} placeholder="username or email" />
              </div>
              <div className="field">
                <label htmlFor="ed-url">Website</label>
                <input id="ed-url" type="text" value={draft.url || ""}
                  onChange={(e) => update({ url: e.target.value })} placeholder="example.com" />
              </div>
            </div>

            <div className="field">
              <label htmlFor="ed-pw">Password</label>
              {revealed ? (
                <>
                  <div className="row">
                    <input id="ed-pw" type="text" className="mono" value={draft.password || ""}
                      onChange={(e) => update({ password: e.target.value })} placeholder="password" />
                    <button className="icon-btn" title="Generate"
                      onClick={() => update({ password: generatePassword(20) })}>
                      <RefreshIcon size={16} />
                    </button>
                    <button className="icon-btn" title="Copy"
                      onClick={() => {
                        navigator.clipboard.writeText(draft.password || "");
                        toast.success("Password copied");
                      }}>
                      <CopyIcon size={16} />
                    </button>
                    <button className="icon-btn" title="Hide" onClick={() => setRevealed(false)}>
                      <EyeOffIcon size={16} />
                    </button>
                  </div>
                  {draft.password && (
                    <div className="row" style={{ marginTop: "var(--s-2)" }}>
                      <div className="progress grow"><span style={{ width: `${strength.score * 25}%` }} /></div>
                      <span className="tiny" style={{ color: `var(--${strength.tone})` }}>{strength.label}</span>
                    </div>
                  )}
                </>
              ) : (
                <button className="btn btn-ghost btn-block" onClick={() => setReauth(true)}>
                  <EyeIcon size={15} /> Authenticate to reveal
                </button>
              )}
              {!revealed && (
                <div className="hint-text">
                  The saved password isn't loaded into this page until you unlock it. Editing
                  the other fields and saving leaves it untouched.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="field">
          <div className="row between">
            <label htmlFor="ed-content">{isPassword ? "Notes" : "Content"}</label>
            <div className="row" style={{ gap: 2 }}>
              <button className={`icon-btn ${listening ? "active" : ""}`} onClick={toggleVoice}
                title={listening ? "Stop dictation" : "Dictate"}>
                <MicIcon size={16} />
              </button>
              {aiReady && (
                <>
                  <button className="btn btn-sm btn-ghost" disabled={busy === "title"}
                    onClick={() => runAssist("title")}>
                    {busy === "title" ? <span className="spinner" /> : <SparkleIcon size={13} />} Title
                  </button>
                  <button className="btn btn-sm btn-ghost" disabled={busy === "tags"}
                    onClick={() => runAssist("tags")}>
                    {busy === "tags" ? <span className="spinner" /> : <SparkleIcon size={13} />} Tags
                  </button>
                  <button className="btn btn-sm btn-ghost" disabled={busy === "summarize"}
                    onClick={() => runAssist("summarize")}>
                    {busy === "summarize" ? <span className="spinner" /> : <SparkleIcon size={13} />} Summarise
                  </button>
                </>
              )}
            </div>
          </div>
          <textarea id="ed-content" value={draft.content || ""} rows={10}
            onChange={(e) => update({ content: e.target.value })}
            placeholder={listening ? "Listening…" : "Write freely. Dates you mention become reminders."} />
        </div>

        <div className="field">
          <label>Tags</label>
          <div className="row wrap" style={{ gap: "var(--s-2)" }}>
            {(draft.tags || []).map((t) => (
              <span className="tag" key={t}>
                {t}
                <button onClick={() => update({ tags: draft.tags.filter((x) => x !== t) })}
                  aria-label={`Remove tag ${t}`}>×</button>
              </span>
            ))}
            <input type="text" value={tagInput} style={{ width: 150 }}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
              onBlur={addTag}
              placeholder="add a tag…" />
          </div>
        </div>

        {detected.length > 0 && (
          <div className="panel" style={{ padding: "var(--s-3) var(--s-4)", background: "var(--info-soft)", border: "none" }}>
            <div className="row wrap" style={{ gap: "var(--s-2)" }}>
              <BellIcon size={15} />
              <span className="small">
                Found {detected.length === 1 ? "a date" : "dates"}:{" "}
                {detected.map((d) => d.matchText).join(", ")}
              </span>
              {!draft.alertEnabled && (
                <button className="chip" onClick={() => update({
                  alertEnabled: true,
                  alertDate: toInputValue(detected[0].alertDate),
                })}>
                  Remind me {fmtDate(detected[0].alertDate)}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="field-row" style={{ alignItems: "flex-end" }}>
          <label className="checkbox" style={{ flex: "none" }}>
            <input type="checkbox" checked={!!draft.alertEnabled}
              onChange={(e) => update({
                alertEnabled: e.target.checked,
                alertDate: e.target.checked
                  ? draft.alertDate || draft.suggestedAlert || toInputValue(new Date())
                  : "",
              })} />
            Set a reminder
          </label>
          {draft.alertEnabled && (
            <div className="field">
              <label htmlFor="ed-alert">Remind me on</label>
              <input id="ed-alert" type="date" value={draft.alertDate || ""}
                onChange={(e) => update({ alertDate: e.target.value })} />
            </div>
          )}
        </div>
      </Modal>

      {reauth && (
        <ReauthDialog
          onCancel={() => setReauth(false)}
          onVerify={revealPassword}
          onBiometricPass={() => { setRevealed(true); setReauth(false); }}
        />
      )}

      {confirmDiscard && (
        <ConfirmDialog
          title="Discard your changes?"
          message="This entry has unsaved edits. Closing now throws them away."
          confirmLabel="Discard"
          danger
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={onClose}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this entry?"
          message="It will be removed from this device and from every device you sync with."
          confirmLabel="Delete"
          danger
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await remove(entry.id);
            toast.success("Entry deleted");
            onClose();
          }}
        />
      )}
    </>
  );
}

/** Master-password (or biometric) check before a stored secret is shown. */
function ReauthDialog({ onCancel, onVerify, onBiometricPass }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const canBio = Biometric.canReveal?.();

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onVerify(password);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Confirm it's you" size="narrow" onClose={onCancel}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <div className="grow" />
          <button className="btn btn-primary" onClick={submit} disabled={busy || !password}>
            Reveal
          </button>
        </>
      }>
      <form onSubmit={submit} className="col" style={{ gap: "var(--s-4)" }}>
        <p className="small secondary">
          Stored passwords stay locked until you confirm your master password.
        </p>
        <div className="field">
          <label htmlFor="reauth-pw">Master password</label>
          <input id="reauth-pw" type="password" value={password} autoFocus
            onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="error-text">{error}</div>
        {canBio && (
          <button type="button" className="btn btn-ghost btn-block"
            onClick={async () => {
              setError("");
              try {
                if (await Biometric.verifyPresence()) onBiometricPass();
                else setError("Biometric check was declined.");
              } catch (err) { setError(err.message); }
            }}>
            <FingerprintIcon size={15} /> Use biometrics instead
          </button>
        )}
      </form>
    </Modal>
  );
}
